import { useState, useMemo } from "react";
import ModeToggle from "../ModeToggle/ModeToggle";
import DesktopProtocolRow from "./DesktopProtocolRow";
import ProtocolEntryList from "./ProtocolEntryList";
import AddSupplementRow from "./AddSupplementRow";
import AddInterventionRow from "./AddInterventionRow";
import RegenerateChoiceModal from "./RegenerateChoiceModal";
import { useProtocolEditor } from "../../hooks/useProtocolEditor";
import { getProductName, normalizeEntrySource } from "../../util/ProtocolUtil";
import ComplianceBar from "../../compliance/ComplianceBar";
import ComplianceNotes from "../../compliance/ComplianceNotes";
import NotificationBanner from "../../compliance/NotificationBanner";
import { useCompliance } from "../../compliance/useComplianceState";

function DesktopProtocol({
    recommendations,
    recommendationMode,
    setRecommendationMode,
    loadingRecommendations,
    filteredTests,
    selectedAlternatives,
    handleSelectAlternative,
    handleGetRecommendations,
    handleGetAllDefaults,
    highlightedRecommendations,
    bloodTestFiles,
    selectedFile,
    doctorViewMode,
    demo = false,
    patientId,
    userId,
    savedProtocol,
    protocolLocked,
    effectiveLocked,
    hasActiveDoctor,
    lockedByName,
    lockLoading,
    saveProtocol,
    toggleLock,
    canRegenerate,
    refreshUserFiles,
    loadingFiles,
}) {
    // Regenerate Replace-vs-Add choice
    const [regenChoiceOpen, setRegenChoiceOpen] = useState(false);
    const [regenerateMode, setRegenerateMode] = useState('replace');

    const {
        saving, doseEdits, allItems,
        patientNotes, setPatientNotes, doctorNotes, setDoctorNotes,
        existingNames, exportRecommendations,
        isLockedPatient, hasRecommendations, hasSavedEntries,
        canEdit, canRemove, canAdd, isLocked, resolveItemAuthor,
        handleDoseEdit, handleSelectAlternativeAndReset,
        handleAddSupplement, handleAddIntervention, handleRemove, handleReorder,
        handleSave, handleSaveNotes,
    } = useProtocolEditor({
        recommendations,
        recommendationMode,
        selectedAlternatives,
        handleSelectAlternative,
        savedProtocol,
        saveProtocol,
        bloodTestFiles,
        protocolLocked,
        doctorViewMode,
        effectiveLocked,
        hasActiveDoctor,
        regenerateMode,
    });

    // Generated rows are always backend-sourced; manual/intervention rows carry
    // their own entry. Patients may add their own entries even when locked.
    const canEditProtocol = canAdd;

    // Regenerate: show Replace-vs-Add only when prior saved entries exist.
    const requestRegenerate = () => {
        if (hasSavedEntries) {
            setRegenChoiceOpen(true);
        } else {
            handleGetRecommendations('replace');
        }
    };
    const runRegenerate = (mode) => {
        setRegenChoiceOpen(false);
        setRegenerateMode(mode);
        handleGetRecommendations(mode);
    };

    // Map saved protocol entries to compliance supplement format — split daily vs weekly
    const { dailyComplianceSupplements, weeklyComplianceSupplements } = useMemo(() => {
        const entries = savedProtocol?.entries;
        if (!entries?.length) return { dailyComplianceSupplements: [], weeklyComplianceSupplements: [] };
        const daily = [];
        const weekly = [];
        for (const e of entries) {
            const item = {
                category: e.category,
                supplement: e.name,
                dose: (e.dose ?? 1) * (e.frequency ?? 1),
                unit: e.unit,
                frequency: e.frequency ?? 1,
                instructions: e.emptyStomach ? 'Take on empty stomach' : e.instructions || null,
            };
            if (e.freqUnit === 'week') {
                weekly.push(item);
            } else {
                daily.push(item);
            }
        }
        return { dailyComplianceSupplements: daily, weeklyComplianceSupplements: weekly };
    }, [savedProtocol]);

    const compliance = useCompliance(dailyComplianceSupplements, {
        patientId, userId, weeklySupplements: weeklyComplianceSupplements,
    });

    // Protocol mode: compliance (default) vs edit
    const [protocolMode, setProtocolMode] = useState('compliance');
    const inComplianceMode = protocolMode === 'compliance';

    // Show/hide completed items in compliance mode (doctors — and the demo — see all by default)
    const [showCompleted, setShowCompleted] = useState(demo || doctorViewMode);
    const [filterSupplements, setFilterSupplements] = useState(false);
    const [filterActivity, setFilterActivity] = useState(false);

    // --- Desktop drag and drop ---
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (index) => (e) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (index) => (e) => {
        e.preventDefault();
        if (dragIndex !== null && dragIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDrop = (dropIndex) => (e) => {
        e.preventDefault();
        if (dragIndex === null || dragIndex === dropIndex) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        handleReorder(dragIndex, dropIndex);
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div className="desktop-protocol">
            <div className="protocol-header">
                <h2>Protocol</h2>
                <div className="protocol-header-actions">
                    <button
                        className="protocol-refresh-btn"
                        onClick={() => refreshUserFiles(true)}
                        disabled={loadingFiles}
                        title="Refresh protocol from server"
                    >
                        {loadingFiles ? (
                            <svg className="protocol-refresh-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 11-6.219-8.56" />
                            </svg>
                        ) : (
                            <span style={{ fontSize: '18px', lineHeight: 1 }}>&#x21bb;</span>
                        )}
                    </button>
                    {/* hasSavedEntries keeps Save reachable after every entry is removed:
                        the list is empty but that emptiness still needs persisting. */}
                    {canEditProtocol && (hasRecommendations || hasSavedEntries) && (
                        <button
                            className={`protocol-mode-btn ${!inComplianceMode ? 'protocol-mode-btn--active' : ''}`}
                            onClick={inComplianceMode
                                ? () => setProtocolMode('edit')
                                : async () => { await handleSave(); setProtocolMode('compliance'); }}
                            disabled={!inComplianceMode && saving}
                        >
                            {inComplianceMode ? 'Edit' : (saving ? 'Saving...' : 'Save')}
                        </button>
                    )}
                    {doctorViewMode && (
                        <label className="protocol-lock-toggle" title={protocolLocked ? 'Unlock protocol for patient' : 'Lock protocol so only you control it'}>
                            <input
                                type="checkbox"
                                checked={protocolLocked}
                                onChange={() => toggleLock(patientId, !protocolLocked)}
                                disabled={lockLoading}
                            />
                            <span>{lockLoading ? 'Updating...' : protocolLocked ? 'Locked' : 'Unlocked'}</span>
                        </label>
                    )}
                </div>
            </div>

            {inComplianceMode && !doctorViewMode && <NotificationBanner />}
            {inComplianceMode && (dailyComplianceSupplements.length + weeklyComplianceSupplements.length) > 0 && (
                <ComplianceBar
                    checkedCount={compliance.todayCheckedCount}
                    totalCount={compliance.totalCount}
                    weeklyCheckedCount={compliance.weeklyCheckedCount}
                    weeklyTotalCount={compliance.weeklyTotalCount}
                    streak={compliance.streak}
                    complianceRate={compliance.complianceRate}
                    history={compliance.history}
                    selectedDate={compliance.selectedDate}
                    onSelectDate={compliance.setSelectedDate}
                    isSelectedComplete={compliance.isSelectedComplete}
                    onCheckAll={doctorViewMode ? undefined : compliance.checkAll}
                />
            )}

            {isLockedPatient && (
                <div className="protocol-locked-banner">
                    Protocol managed by {lockedByName ? `Dr. ${lockedByName}` : 'your doctor'}.
                </div>
            )}

            {canRegenerate && !isLockedPatient && (
                <div className="protocol-stale-notice">
                    Newer lab results available.{' '}
                    <button
                        onClick={requestRegenerate}
                        disabled={loadingRecommendations}
                        className="regenerate-link"
                    >
                        {loadingRecommendations ? 'Analyzing...' : 'Regenerate protocol'}
                    </button>
                </div>
            )}

            {hasRecommendations ? (
                <>
                    {canEditProtocol && !inComplianceMode && (
                        <div className="protocol-controls">
                            <ModeToggle mode={recommendationMode} onChange={setRecommendationMode} />
                            <button onClick={handleGetAllDefaults} className="get-defaults-btn">
                                Get Defaults
                            </button>
                        </div>
                    )}
                    <div className="protocol-rows">
                        {(() => {
                            const supplementItems = allItems.filter(item => item.type !== 'intervention');
                            const interventionItems = allItems.filter(item => item.type === 'intervention');
                            const dailyActivities = interventionItems.filter(item => item.entry.freqUnit !== 'week');
                            const weeklyActivities = interventionItems.filter(item => item.entry.freqUnit === 'week');

                            // In compliance mode, daily activities group with supplements
                            const dailySection = inComplianceMode
                                ? [...supplementItems, ...dailyActivities]
                                : supplementItems;

                            // Check if an item is completed in compliance mode
                            const isItemChecked = (item) => {
                                if (!inComplianceMode) return false;
                                if (item.type === 'intervention') {
                                    return item.entry.freqUnit === 'week'
                                        ? (compliance.selectedWeeklyChecklist[item.entry.name] || false)
                                        : (compliance.selectedChecklist[item.entry.name] || false);
                                }
                                const name = item.type === 'generated'
                                    ? getProductName(item.group.products.find(p => getProductName(p) === selectedAlternatives[item.group.category]) || item.group.products[0])
                                    : item.entry.name;
                                return compliance.selectedChecklist[name] || false;
                            };

                            // Helper to render an intervention row
                            const renderInterventionRow = (item, i, globalIdx) => {
                                const cat = item.entry.category;
                                const isWeekly = item.entry.freqUnit === 'week';
                                const gateEntry = { author: resolveItemAuthor(item) };
                                const rowCanEdit = canEdit(gateEntry);
                                const rowCanRemove = canRemove(gateEntry);
                                const rowLocked = isLocked(gateEntry);
                                const complianceProps = inComplianceMode ? {
                                    complianceMode: true,
                                    complianceChecked: isWeekly
                                        ? (compliance.selectedWeeklyChecklist[item.entry.name] || false)
                                        : (compliance.selectedChecklist[item.entry.name] || false),
                                    onComplianceToggle: doctorViewMode ? undefined
                                        : isWeekly ? compliance.toggleWeeklyItem : compliance.toggleItem,
                                    weeklyProgress: isWeekly ? compliance.weeklyProgress[item.entry.name] : undefined,
                                } : {};
                                return (
                                    <DesktopProtocolRow
                                        key={item.key}
                                        category={cat}
                                        categoryWeight={0}
                                        products={[{
                                            product_details: {
                                                product_name: item.entry.name,
                                                min_daily_dose: item.entry.dose,
                                                unit: item.entry.unit,
                                                freq_unit: item.entry.freqUnit,
                                                empty_stomach: null,
                                                sets: item.entry.sets || null,
                                                _entryFrequency: item.entry.frequency,
                                            },
                                            fulfills_categories: [{ category: cat, weight: 0 }],
                                            total_weight: 0,
                                        }]}
                                        mode={recommendationMode}
                                        priority={i + 1}
                                        selectedProduct={null}
                                        onSelectProduct={() => {}}
                                        highlighted={false}
                                        doseEdit={doseEdits[item.key]}
                                        onDoseEdit={(_, edit) => handleDoseEdit(item.key, edit)}
                                        doctorViewMode={doctorViewMode}
                                        readOnly={!rowCanEdit}
                                        onDragStart={handleDragStart(globalIdx)}
                                        onDragOver={handleDragOver(globalIdx)}
                                        onDrop={handleDrop(globalIdx)}
                                        onDragEnd={handleDragEnd}
                                        isDraggedOver={dragOverIndex === globalIdx}
                                        onRemove={rowCanRemove ? () => handleRemove(item.key) : undefined}
                                        entryType="intervention"
                                        locked={rowLocked}
                                        lockedByName={lockedByName}
                                        {...complianceProps}
                                    />
                                );
                            };

                            // Filter completed items in compliance mode
                            const filterItems = (items) =>
                                inComplianceMode && !showCompleted
                                    ? items.filter(item => !isItemChecked(item))
                                    : items;

                            const hasAnyChecked = (items) => items.some(item => isItemChecked(item));

                            // Section toggle button
                            const renderToggle = (items) => {
                                if (!inComplianceMode || !hasAnyChecked(items)) return null;
                                return (
                                    <button
                                        type="button"
                                        className={`protocol-section-toggle ${showCompleted ? 'protocol-section-toggle--active' : ''}`}
                                        onClick={() => setShowCompleted(prev => !prev)}
                                    >
                                        {showCompleted ? 'show remaining' : 'show all'}
                                    </button>
                                );
                            };

                            // Render a supplement/manual-entry row
                            const renderSupplementRow = (item, i) => {
                                const cat = item.type === 'generated' ? item.group.category : item.entry.category;
                                // Gating is by AUTHOR (resolved per item), independent of source.
                                const gateEntry = { author: resolveItemAuthor(item) };
                                const rowCanEdit = canEdit(gateEntry);
                                const rowCanRemove = canRemove(gateEntry);
                                const rowLocked = isLocked(gateEntry);
                                const suppName = item.type === 'generated'
                                    ? getProductName(item.group.products.find(p => getProductName(p) === selectedAlternatives[cat]) || item.group.products[0])
                                    : item.entry.name;
                                const complianceProps = inComplianceMode ? {
                                    complianceMode: true,
                                    complianceChecked: compliance.selectedChecklist[suppName] || false,
                                    onComplianceToggle: doctorViewMode ? undefined : compliance.toggleItem,
                                } : {};
                                return item.type === 'generated' ? (
                                    <DesktopProtocolRow
                                        key={item.key}
                                        category={cat}
                                        categoryWeight={item.group.weight}
                                        products={item.group.products}
                                        mode={recommendationMode}
                                        priority={i + 1}
                                        selectedProduct={selectedAlternatives[cat] || null}
                                        onSelectProduct={(name) => handleSelectAlternativeAndReset(cat, name)}
                                        highlighted={highlightedRecommendations.includes(cat)}
                                        doseEdit={doseEdits[item.key]}
                                        onDoseEdit={handleDoseEdit}
                                        doctorViewMode={doctorViewMode}
                                        readOnly={!rowCanEdit}
                                        onDragStart={handleDragStart(i)}
                                        onDragOver={handleDragOver(i)}
                                        onDrop={handleDrop(i)}
                                        onDragEnd={handleDragEnd}
                                        isDraggedOver={dragOverIndex === i}
                                        onRemove={rowCanRemove ? () => handleRemove(item.key) : undefined}
                                        locked={rowLocked}
                                        lockedByName={lockedByName}
                                        {...complianceProps}
                                    />
                                ) : (
                                    <DesktopProtocolRow
                                        key={item.key}
                                        category={cat}
                                        categoryWeight={0}
                                        products={[{
                                            product_details: {
                                                product_name: item.entry.name,
                                                min_daily_dose: item.entry.dose,
                                                unit: item.entry.unit,
                                                freq_unit: item.entry.freqUnit,
                                                empty_stomach: item.entry.emptyStomach,
                                                sets: item.entry.sets || null,
                                                _entryFrequency: item.entry.frequency,
                                            },
                                            fulfills_categories: [{ category: cat, weight: 0 }],
                                            total_weight: 0,
                                        }]}
                                        mode={recommendationMode}
                                        priority={i + 1}
                                        selectedProduct={null}
                                        onSelectProduct={() => {}}
                                        highlighted={false}
                                        doseEdit={doseEdits[item.key]}
                                        onDoseEdit={(_, edit) => handleDoseEdit(item.key, edit)}
                                        doctorViewMode={doctorViewMode}
                                        readOnly={!rowCanEdit}
                                        onDragStart={handleDragStart(i)}
                                        onDragOver={handleDragOver(i)}
                                        onDrop={handleDrop(i)}
                                        onDragEnd={handleDragEnd}
                                        isDraggedOver={dragOverIndex === i}
                                        onRemove={rowCanRemove ? () => handleRemove(item.key) : undefined}
                                        locked={rowLocked}
                                        lockedByName={lockedByName}
                                        {...complianceProps}
                                    />
                                );
                            };

                            if (inComplianceMode) {
                                // --- Compliance mode: Daily / Weekly sections ---
                                const allDailyItems = [...supplementItems, ...dailyActivities];
                                const hasBothTypes = supplementItems.length > 0 && (dailyActivities.length > 0 || weeklyActivities.length > 0);

                                // Type filter: none selected = show all, otherwise show only selected types
                                const noFiltersActive = !filterSupplements && !filterActivity;
                                const typeFilter = (item) => {
                                    if (noFiltersActive) return true;
                                    if (item.type === 'intervention') return filterActivity;
                                    return filterSupplements;
                                };

                                const visibleDailyItems = filterItems(allDailyItems).filter(typeFilter);
                                const visibleWeekly = filterItems(weeklyActivities).filter(typeFilter);

                                return (<>
                                    {/* Type filter bar — above both sections */}
                                    {hasBothTypes && (
                                        <div className="protocol-type-filters">
                                            <button
                                                type="button"
                                                className={`protocol-type-filter ${filterSupplements ? 'protocol-type-filter--supplement' : 'protocol-type-filter--inactive'}`}
                                                onClick={() => setFilterSupplements(prev => !prev)}
                                            >
                                                Supplements
                                            </button>
                                            <button
                                                type="button"
                                                className={`protocol-type-filter ${filterActivity ? 'protocol-type-filter--activity' : 'protocol-type-filter--inactive'}`}
                                                onClick={() => setFilterActivity(prev => !prev)}
                                            >
                                                Activity
                                            </button>
                                            {(filterSupplements || filterActivity) && (
                                                <button
                                                    type="button"
                                                    className="protocol-type-filter-clear"
                                                    onClick={() => { setFilterSupplements(false); setFilterActivity(false); }}
                                                >
                                                    Clear Filters
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Daily section — header if entries exist, toggle if any hidden */}
                                    {allDailyItems.length > 0 && (
                                        <div className="protocol-section-divider">
                                            <span className="protocol-section-label">Daily</span>
                                            {renderToggle(allDailyItems)}
                                        </div>
                                    )}
                                    {visibleDailyItems.map((item, i) => {
                                        if (item.type === 'intervention') {
                                            const globalIdx = allItems.indexOf(item);
                                            return renderInterventionRow(item, i, globalIdx);
                                        }
                                        return renderSupplementRow(item, i);
                                    })}

                                    {/* Weekly section — header if entries exist, toggle if any hidden */}
                                    {weeklyActivities.length > 0 && (
                                        <div className="protocol-section-divider">
                                            <span className="protocol-section-label">Weekly</span>
                                            {renderToggle(weeklyActivities)}
                                        </div>
                                    )}
                                    {visibleWeekly.length > 0 && (
                                        <div className="protocol-intervention-section">
                                            {visibleWeekly.map((item, i) => {
                                                const globalIdx = allItems.indexOf(item);
                                                return renderInterventionRow(item, i, globalIdx);
                                            })}
                                        </div>
                                    )}
                                </>);
                            }

                            // --- Edit mode: Supplements / Activity sections ---
                            return (<>
                                {supplementItems.length > 0 && (
                                    <div className="protocol-section-divider">
                                        <span className="protocol-section-label">Supplements</span>
                                    </div>
                                )}
                                {supplementItems.map((item, i) => renderSupplementRow(item, i))}
                                {canAdd && (
                                    <AddSupplementRow
                                        onAdd={handleAddSupplement}
                                        existingNames={existingNames}
                                    />
                                )}

                                {(interventionItems.length > 0 || canAdd) && (
                                    <div className="protocol-section-divider">
                                        <span className="protocol-section-label">Activity</span>
                                    </div>
                                )}
                                {interventionItems.length > 0 && (
                                    <div className="protocol-intervention-section">
                                        {interventionItems.map((item, i) => {
                                            const globalIdx = allItems.indexOf(item);
                                            return renderInterventionRow(item, i, globalIdx);
                                        })}
                                    </div>
                                )}
                                {canAdd && (
                                    <AddInterventionRow
                                        onAdd={handleAddIntervention}
                                    />
                                )}
                            </>);
                        })()}
                    </div>

                    {inComplianceMode && (dailyComplianceSupplements.length + weeklyComplianceSupplements.length) > 0 && (
                        <ComplianceNotes
                            selectedDate={compliance.selectedDate}
                            selectedNote={compliance.selectedNote}
                            onNoteChange={compliance.setSelectedNote}
                            allNotes={compliance.allNotes}
                            readOnly={doctorViewMode}
                        />
                    )}

                    {!inComplianceMode && (
                        <div className="protocol-notes-section">
                            <h3 className="protocol-notes-heading">Notes</h3>
                            {!doctorViewMode ? (
                                <textarea
                                    className="protocol-notes-textarea"
                                    placeholder="Add notes about your protocol..."
                                    value={patientNotes}
                                    onChange={(e) => setPatientNotes(e.target.value)}
                                    rows={3}
                                />
                            ) : patientNotes ? (
                                <p className="protocol-notes-text">{patientNotes}</p>
                            ) : (
                                <p className="protocol-notes-empty">No patient notes.</p>
                            )}
                            {isLockedPatient && (
                                <button
                                    onClick={handleSaveNotes}
                                    className="analyze-btn"
                                    disabled={saving}
                                    style={{ marginTop: 8 }}
                                >
                                    {saving ? 'Saving...' : 'Save Notes'}
                                </button>
                            )}
                        </div>
                    )}
                    {!inComplianceMode && doctorViewMode && (
                        <div className="protocol-notes-section protocol-doctor-notes">
                            <h3 className="protocol-notes-heading">Doctor Notes</h3>
                            <textarea
                                className="protocol-notes-textarea"
                                placeholder="Private notes (not visible to patient)..."
                                value={doctorNotes}
                                onChange={(e) => setDoctorNotes(e.target.value)}
                                rows={3}
                            />
                        </div>
                    )}
                </>
            ) : isLockedPatient ? (
                hasSavedEntries ? (
                    <>
                        <ProtocolEntryList
                            entries={savedProtocol.entries.map(e => normalizeEntrySource(e, savedProtocol))}
                            effectiveLocked={isLockedPatient}
                            lockedByName={lockedByName}
                        />
                        <div className="protocol-notes-section">
                            <h3 className="protocol-notes-heading">Notes</h3>
                            <textarea
                                className="protocol-notes-textarea"
                                placeholder="Add notes about your protocol..."
                                value={patientNotes}
                                onChange={(e) => setPatientNotes(e.target.value)}
                                rows={3}
                            />
                            <button
                                onClick={handleSaveNotes}
                                className="analyze-btn"
                                disabled={saving}
                                style={{ marginTop: 8 }}
                            >
                                {saving ? 'Saving...' : 'Save Notes'}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="protocol-empty">
                        <p>{lockedByName ? `Dr. ${lockedByName} is` : 'Your doctor is'} preparing your protocol.</p>
                    </div>
                )
            ) : (
                <div className="protocol-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" />
                        <path d="M9 3v4" />
                        <path d="M15 3v4" />
                        <path d="M9 14h.01" />
                        <path d="M15 14h.01" />
                        <path d="M9 18h.01" />
                        <path d="M15 18h.01" />
                        <path d="M3 21h18" />
                    </svg>
                    <p>{doctorViewMode
                        ? 'No protocol generated yet. Click "Generate" to analyze lab results.'
                        : 'Get personalized supplement recommendations based on your lab results.'
                    }</p>
                    <button
                        onClick={requestRegenerate}
                        disabled={loadingRecommendations || lockLoading || !filteredTests?.length}
                        className="analyze-btn"
                    >
                        {loadingRecommendations ? 'Analyzing...' : lockLoading ? 'Checking...' : doctorViewMode ? 'Generate' : 'Analyze Lab Results'}
                    </button>
                </div>
            )}

            {regenChoiceOpen && (
                <RegenerateChoiceModal
                    onClose={() => setRegenChoiceOpen(false)}
                    onChoose={runRegenerate}
                />
            )}
        </div>
    );
}

export default DesktopProtocol;
